import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

/**
 * A book "spine" — the design's cover treatment (A2 / C1 / F1).
 *
 * When a real cover image is available (`coverUrl`) it is shown as-is. Otherwise
 * this renders a tinted spine carrying the title — the design's own default
 * treatment, so it is faithful rather than a placeholder, and it avoids leaving
 * a blank box when a book (or group) has no cover art yet.
 */

const Spine = styled.div<{ $w: number; $h: number; $from: string; $to: string }>`
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  border-radius: 6px;
  flex-shrink: 0;
  padding: 8px;
  font-size: ${({ $w }) => Math.max(8, Math.round($w * 0.185))}px;
  font-weight: 800;
  line-height: 1.2;
  color: #eef6f5;
  overflow: hidden;
  background: ${({ $from, $to }) => `linear-gradient(150deg, ${$from}, ${$to})`};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
`;

// A real cover image, sized to the same footprint as the tinted spine.
const CoverImg = styled.img<{ $w: number; $h: number }>`
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  border-radius: 6px;
  flex-shrink: 0;
  object-fit: cover;
  display: block;
  background: #e7e3d9;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
`;

// Muted, book-cloth palette — deliberately dark so the title stays legible.
const PALETTE: Array<[string, string]> = [
  ['#1f5c57', '#0f3b38'],
  ['#7a3b1c', '#4a2410'],
  ['#2b4a6f', '#152b44'],
  ['#5c3a63', '#33203a'],
  ['#6b5324', '#3d2f12'],
  ['#1f5c3a', '#0f3b25'],
];

const pick = (seed: string): [string, string] => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
};

type Props = {
  title?: string;
  /** The real cover image to show. When absent, the tinted spine is drawn. */
  coverUrl?: string;
  /** Keeps a given book/group's spine colour stable across screens. */
  seed?: string;
  width?: number;
  height?: number;
};

export const BookSpine: React.FC<Props> = ({
  title,
  coverUrl,
  seed,
  width = 54,
  height = 76,
}) => {
  const { t } = useTranslation();

  // A book's own cover wins; the tinted spine is the default fallback.
  if (coverUrl) {
    return (
      <CoverImg
        $w={width}
        $h={height}
        src={coverUrl}
        alt={title || ''}
        title={title}
        loading="lazy"
        data-testid="book-spine-cover"
      />
    );
  }

  const [from, to] = pick(seed || title || 'book');
  return (
    <Spine
      $w={width}
      $h={height}
      $from={from}
      $to={to}
      title={title}
      data-testid="book-spine"
    >
      {title || t('CritiqueUntitled', 'Untitled')}
    </Spine>
  );
};

export default BookSpine;
